/**
 * Курсор ячейки и полная карта клавиш (§5.1, §5.3).
 *
 * Модель спеки: всегда есть одна активная ячейка, правка открывается явно —
 * Enter, двойной клик, Alt+↓ или клик по уже активной ячейке. Печать символа
 * правку НЕ начинает (решение против «правок-призраков»); персональная галочка
 * «как в Google Sheets» включает обратное поведение и тогда символ приезжает
 * в редактор как начальное значение.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'

export interface CellPos { row: number; col: number }

export interface CellCursorOptions {
  rowCount: number
  colCount: number
  /** Можно ли поставить курсор: неприменимые и служебные ячейки пропускаются. */
  isFocusable: (row: number, col: number) => boolean
  /** Можно ли открыть редактор: права, канал, роль (§11.4). */
  canEdit: (row: number, col: number) => boolean
  /** Пустая ли ячейка — для `]` и `[` (§5.3). */
  isEmpty: (row: number, col: number) => boolean
  /** Ячейка-флаг: Space и Enter переключают значение сразу, без редактора (§5.2). */
  isToggle: (row: number, col: number) => boolean
  /** Переключение флага — правка в черновик. */
  toggleBool: (pos: CellPos) => void
  /** Space в gutter выделяет строку (§5.3). */
  selectRow: (row: number) => void
  /** E — развернуть/свернуть бренд, Shift+E — все (§5.3). */
  expandRow: (row: number, all: boolean) => void
  /** Прокрутка к строке перед постановкой фокуса: строка могла быть размонтирована. */
  scrollToRow: (row: number) => void
  /** Нет больше пустых в колонке — сообщение в статус-зону. */
  onNoMoreEmpty?: () => void
  /** «как в Google Sheets»: печать символа начинает правку. */
  typeToEdit: boolean
  rowsPerPage: number
}

export interface CellCursorApi {
  cursor: CellPos | null
  editing: boolean
  /** Символ, с которого начали печатать в режиме «как в Google Sheets». */
  seed: string | null
  isActive: (row: number, col: number) => boolean
  isEditing: (row: number, col: number) => boolean
  focusCell: (pos: CellPos, options?: { edit?: boolean }) => void
  clear: () => void
  startEdit: (pos?: CellPos) => void
  stopEdit: () => void
  /** После фиксации: шаг по строкам с повторным открытием редактора (§5.3). */
  commitAndStep: (direction: 1 | -1, openEditor: boolean) => void
  /** Фиксация и шаг вправо без открытия редактора — Tab в редакторе (§5.3). */
  commitAndRight: () => void
  nextEmpty: (direction: 1 | -1) => void
  handleKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void
}

export function useCellCursor(options: CellCursorOptions): CellCursorApi {
  const {
    rowCount, colCount, isFocusable, canEdit, isEmpty, toggleBool, selectRow,
    expandRow, scrollToRow, onNoMoreEmpty, typeToEdit, rowsPerPage,
  } = options

  const [cursor, setCursor] = useState<CellPos | null>(null)
  const [editing, setEditing] = useState(false)
  const [seed, setSeed] = useState<string | null>(null)

  // Свежие колбэки в одном месте: карта клавиш не пересобирается на каждый рендер.
  const ref = useRef(options)
  ref.current = options

  /** Ближайшая доступная колонка в строке в заданном направлении. */
  const stepCol = useCallback((row: number, from: number, direction: 1 | -1): number | null => {
    for (let col = from + direction; col >= 0 && col < colCount; col += direction) {
      if (ref.current.isFocusable(row, col)) return col
    }
    return null
  }, [colCount])

  const firstCol = useCallback((row: number, direction: 1 | -1): number | null => {
    const from = direction === 1 ? -1 : colCount
    return stepCol(row, from, direction)
  }, [colCount, stepCol])

  /** Ближайшая строка, в которой эта колонка применима. */
  const stepRow = useCallback((col: number, from: number, direction: 1 | -1): number | null => {
    for (let row = from + direction; row >= 0 && row < rowCount; row += direction) {
      if (ref.current.isFocusable(row, col)) return row
    }
    return null
  }, [rowCount])

  const focusCell = useCallback((pos: CellPos, opts?: { edit?: boolean }) => {
    setCursor(pos)
    setSeed(null)
    setEditing(opts?.edit === true && ref.current.canEdit(pos.row, pos.col))
    ref.current.scrollToRow(pos.row)
  }, [])

  const clear = useCallback(() => {
    setEditing(false)
    setSeed(null)
    setCursor(null)
  }, [])

  const startEdit = useCallback((pos?: CellPos) => {
    const target = pos ?? cursor
    if (!target) return
    if (!ref.current.canEdit(target.row, target.col)) return
    if (ref.current.isToggle(target.row, target.col)) { ref.current.toggleBool(target); return }
    setCursor(target)
    setSeed(null)
    setEditing(true)
  }, [cursor])

  const stopEdit = useCallback(() => {
    setEditing(false)
    setSeed(null)
  }, [])

  const commitAndStep = useCallback((direction: 1 | -1, openEditor: boolean) => {
    setSeed(null)
    const current = cursor
    if (!current) { setEditing(false); return }
    const row = stepRow(current.col, current.row, direction)
    if (row === null) { setEditing(false); return }
    setCursor({ row, col: current.col })
    setEditing(openEditor && ref.current.canEdit(row, current.col))
    ref.current.scrollToRow(row)
  }, [cursor, stepRow])

  const commitAndRight = useCallback(() => {
    setSeed(null)
    setEditing(false)
    const current = cursor
    if (!current) return
    const col = stepCol(current.row, current.col, 1)
    if (col !== null) { setCursor({ row: current.row, col }); return }
    const row = stepRow(0, current.row, 1)
    if (row === null) return
    const next = firstCol(row, 1)
    if (next !== null) { setCursor({ row, col: next }); ref.current.scrollToRow(row) }
  }, [cursor, stepCol, stepRow, firstCol])

  /** `]` / `[` — следующая пустая ячейка в текущей колонке внутри снимка (§5.3). */
  const nextEmpty = useCallback((direction: 1 | -1) => {
    const current = cursor
    if (!current) return
    for (let row = current.row + direction; row >= 0 && row < rowCount; row += direction) {
      if (!ref.current.isFocusable(row, current.col)) continue
      if (!ref.current.isEmpty(row, current.col)) continue
      setCursor({ row, col: current.col })
      setSeed(null)
      setEditing(ref.current.canEdit(row, current.col))
      ref.current.scrollToRow(row)
      return
    }
    ref.current.onNoMoreEmpty?.()
  }, [cursor, rowCount])

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    const current = cursor
    const { key, code } = event

    /* Текстовое поле внутри таблицы — поповер комментария, отчёт о выполнении,
       ответ на задачу — ест клавиши само. React поднимает события и из
       порталов, поэтому пробел из такого поля долетал сюда и съедался
       строкой `case ' '`: буквы печатались, а пробел — нет. */
    const target = event.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return
    }

    // Внутри открытого редактора клавиши обрабатывает сам редактор (§5.3).
    if (editing) return

    if (key === 'Escape') {
      if (current) { event.preventDefault(); clear() }
      return
    }

    if (!current) {
      // Первое движение с клавиатуры ставит курсор в первую доступную ячейку.
      if (key.startsWith('Arrow') || key === 'Tab' || key === 'Enter') {
        const row = stepRow(0, -1, 1) ?? 0
        const col = firstCol(row, 1)
        if (col !== null) { event.preventDefault(); focusCell({ row, col }) }
      }
      return
    }

    const move = (pos: CellPos | null) => {
      if (!pos) return
      event.preventDefault()
      setCursor(pos)
      ref.current.scrollToRow(pos.row)
    }

    switch (key) {
      case 'ArrowLeft': {
        const col = stepCol(current.row, current.col, -1)
        return move(col === null ? null : { row: current.row, col })
      }
      case 'ArrowRight': {
        const col = stepCol(current.row, current.col, 1)
        return move(col === null ? null : { row: current.row, col })
      }
      case 'ArrowUp': {
        if (event.altKey) { event.preventDefault(); return }
        const row = stepRow(current.col, current.row, -1)
        return move(row === null ? null : { row, col: current.col })
      }
      case 'ArrowDown': {
        if (event.altKey) { event.preventDefault(); startEdit(current); return }
        const row = stepRow(current.col, current.row, 1)
        return move(row === null ? null : { row, col: current.col })
      }
      case 'Tab': {
        event.preventDefault()
        if (event.shiftKey) {
          const col = stepCol(current.row, current.col, -1)
          if (col !== null) return move({ row: current.row, col })
          const row = stepRow(0, current.row, -1)
          if (row === null) return
          const last = firstCol(row, -1)
          return move(last === null ? null : { row, col: last })
        }
        const col = stepCol(current.row, current.col, 1)
        if (col !== null) return move({ row: current.row, col })
        const row = stepRow(0, current.row, 1)
        if (row === null) return
        const first = firstCol(row, 1)
        return move(first === null ? null : { row, col: first })
      }
      case 'Home': {
        event.preventDefault()
        if (event.ctrlKey || event.metaKey) {
          const row = stepRow(current.col, -1, 1)
          return move(row === null ? null : { row, col: current.col })
        }
        const col = firstCol(current.row, 1)
        return move(col === null ? null : { row: current.row, col })
      }
      case 'End': {
        event.preventDefault()
        if (event.ctrlKey || event.metaKey) {
          const row = stepRow(current.col, rowCount, -1)
          return move(row === null ? null : { row, col: current.col })
        }
        const col = firstCol(current.row, -1)
        return move(col === null ? null : { row: current.row, col })
      }
      case 'PageUp':
      case 'PageDown': {
        event.preventDefault()
        const direction = key === 'PageDown' ? 1 : -1
        const target = Math.min(rowCount - 1, Math.max(0, current.row + direction * rowsPerPage))
        const row = ref.current.isFocusable(target, current.col)
          ? target
          : stepRow(current.col, target, direction) ?? stepRow(current.col, target, direction === 1 ? -1 : 1)
        return move(row === null ? null : { row, col: current.col })
      }
      case 'Enter': {
        event.preventDefault()
        if (ref.current.isToggle(current.row, current.col)) {
          if (ref.current.canEdit(current.row, current.col)) toggleBool(current)
          return
        }
        startEdit(current)
        return
      }
      case ' ': {
        event.preventDefault()
        if (current.col === 0) { selectRow(current.row); return }
        if (ref.current.isToggle(current.row, current.col)
          && ref.current.canEdit(current.row, current.col)) toggleBool(current)
        return
      }
      default:
        break
    }

    if (event.ctrlKey || event.metaKey) return

    if (code === 'BracketRight') { event.preventDefault(); nextEmpty(1); return }
    if (code === 'BracketLeft') { event.preventDefault(); nextEmpty(-1); return }
    if (code === 'KeyE' && !event.altKey) {
      event.preventDefault()
      expandRow(current.row, event.shiftKey)
      return
    }

    // Печать символа: правку начинает только персональный режим «как в Sheets».
    if (typeToEdit && key.length === 1 && !event.altKey) {
      if (!ref.current.canEdit(current.row, current.col)) return
      event.preventDefault()
      setSeed(key)
      setEditing(true)
    }
  }, [
    cursor, editing, clear, focusCell, startEdit, stepCol, stepRow, firstCol,
    nextEmpty, rowCount, rowsPerPage, selectRow, toggleBool, expandRow, typeToEdit,
  ])

  // Курсор не должен указывать за пределы изменившегося набора строк.
  useEffect(() => {
    if (!cursor) return
    if (cursor.row < rowCount && cursor.col < colCount) return
    setCursor(null)
    setEditing(false)
  }, [cursor, rowCount, colCount])

  const isActive = useCallback(
    (row: number, col: number) => cursor !== null && cursor.row === row && cursor.col === col,
    [cursor],
  )
  const isEditing = useCallback(
    (row: number, col: number) => editing && isActive(row, col),
    [editing, isActive],
  )

  void isEmpty
  void canEdit
  void isFocusable

  return {
    cursor, editing, seed, isActive, isEditing, focusCell, clear,
    startEdit, stopEdit, commitAndStep, commitAndRight, nextEmpty, handleKeyDown,
  }
}
